import React, { useState, useEffect, createContext } from "react";
import { useDispatch, useSelector } from 'react-redux';
import {CommonDialogBox} from "components";
import { Button, Stack, Box, Divider } from "@mui/material";
import CustomStepper from "components/CustomStepper";
import FormDetails from "./FormDetails";
import FormCoordinates from "./FormCoordinates";
import FormProjection from "./FormProjection";
import FormDimensions from "./FormDimensions";
import ButtonStepper from "components/ButtonStepper";
import { Scrollbars } from "react-custom-scrollbars";
import './style.css';
import {
	closeNewDialog,
	closeEditDialog,
  remoteCreateProject,
  remoteUpdateProject,
  resetProject,
  resetLastRequest,
  removeRequestDone
} from "store/projects";
import {
	addAutomaticObject
} from "store/main/viewObjects";
import _ from "utils/@lodash";
import { useDeepCompareEffect } from 'hooks';
import NotificationModel from 'components/notificationPanel/model/NotificationModel';
import { addNotification } from 'components/notificationPanel/store/dataSlice';
import { updateData } from "services/CacheService";
import EventsEmitter from "events/EventsEmitter";
import { trackEvent } from "components/Tracking";
import { tss } from "tss-react/mui";
import { ReactComponent as Mesh } from 'assets/images/ic_geo-mesh.svg'
import { ReactComponent as PointCloud } from 'assets/images/ic_point-cloud.svg';
import { ReactComponent as Ortho } from 'assets/images/ortho.svg';
import { ReactComponent as Heightmap } from 'assets/images/rawhm.svg';
import { ReactComponent as BlockModel } from 'assets/images/rawbm.svg';
import { useHistory } from 'react-router-dom';

const StepperForm: any[] = [
  FormDetails,
  FormCoordinates,
  FormProjection,
  FormDimensions
]
export const FormContext = createContext({});

const steps = ['Project Details', 'Coordinates', 'Geographic Projection', 'Dimensions'];

const _renderStepContent = (step: number) => {
  const Form = StepperForm[step - 1] ? StepperForm[step - 1] : <div>Not Found</div>
  return <Form />;
}

const formFields = [[
  'name',
  'description',
], [
  'timezone',
  'hdatum',
  'vdatum',
], [
  'projection',
  'utmZone',
  'utmNorthing',
  'falseEasting',
  'falseNorthing',
  'latitudeOrigin',
  'longitudeMeridian',
  'scaleFactor',
  'firstStdParallel',
  'secondStdParallel',
  'amgZone',
  'mgaZone',
  'loZone',
], [
  'voxelSize',
  'units',
  'originX',
  'originY',
  'originZ',
  'minX',
  'minY',
  'minZ',
  'maxX',
  'maxY',
  'maxZ',
]]

const projectHelpLinks = [
  'project-details',
  'project-coordinate-system',
  'project-geographic-projection',
  'project-dimensions'
]
export const useStyles = tss.create(({ theme }) => ({
    objectItem: {
        padding: '10px 5px', 
        borderRadius: '8px', 
        margin: '5px 0 5px 0', 
        flexDirection: 'column', 
        display:'flex', 
        textAlign: 'center', 
        width:'150px', 
        boxShadow: theme.shadows[4],
        "&:hover": {
            backgroundColor: '#cccccc30',
            "& a": {
                color: theme.palette.primary.main
            },
            "& svg": {
                color: theme.palette.primary.main,
                fill: theme.palette.primary.main,
                "& path": {
                    stroke: theme.palette.primary.main,
                }
            },
        },
        '& a': {
            textDecoration : 'none',
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap'
        },
        "& svg": {
            color: theme.palette.text.primary,
            fill: theme.palette.text.primary,
            "& path": {
                stroke: theme.palette.text.primary,
            }
        },
    },
    mainTitle: {
        fontWeight:500, 
        marginBottom: '10px', 
        fontSize: '18px',
        padding: "8px 12px",
        backgroundColor: "rgba(32,32,32,.05)",
        width: "100%",
        "& span": {
            color: theme.palette.primary.main,
            " &.subtitle": {
                color: theme.palette.text.secondary,
                fontSize: '14px',
            }
        },
        display:'flex',
        justifyContent: 'space-between'
    },
    helpText: {
        fontSize: '15px',
        LineWeight: '23px',
        margin: '0px',
        padding: '0px 0px 10px 0px',
        "& ul": {
            columns: 2,
            columnGap: '20px'
        }
    }
}))

const CreateProjectPanel: React.FC = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [isLastStep, setIsLastStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitForm, setSubmitForm] = useState(false);
  const [createStatus, setCreateStatus] = useState(false);
	const dispatch = useDispatch<any>();
	const projects = useSelector((state: any) => state.projects);
  const [formData, setFormData] = useState<any>({});
  const [formPayloadData, setFormPayloadData] = useState<any>({});
  const [defaultValues, setDefaultValues] = useState<any>({
    name: 'Project Name',
    description: '',
  });
  const [currentFields, setCurrentFields] = useState<any>([
    'name',
    'description'
  ]);
  const {
    id:projectId,
		requestDone,
		success,
		error,
		lastRequest,
    lastCreated
  } = projects
  const history = useHistory();

  useEffect(() => {
    if(!projects?.dialogBox?.props?.open) return;
    const data = {...(projects?.dialogBox?.data || {})};
    setFormData(JSON.parse(JSON.stringify(data)));
  }, [projects?.dialogBox]);

  useEffect(() => {
    setCreateStatus(requestDone && lastRequest === 'create' && success);
  }, [requestDone, lastRequest, success]);

  useEffect(() => {
    if (_.isEmpty(formData)) return;

    const data = formFields[activeStep - 1].reduce((data: any, field: string) => ({
        ...data,
        [field]: field in formData ? formData[field] : ''
    }), {});
    if (activeStep === 4) {
      data.voxelSize = parseFloat(data.voxelSize);
      data.originX = parseInt(data.originX);
      data.originY = parseInt(data.originY);
      data.originZ = parseInt(data.originZ);
    }
    setDefaultValues(data);
    setCurrentFields(formFields[activeStep - 1]);
  }, [activeStep, formFields, formData]);

  useEffect(() => {
    setIsLastStep(activeStep === steps.length);
  }, [activeStep, steps]);

  const resetForm = () => {
    dispatch(resetProject());
    setActiveStep(1);
  }

  const closeComposeDialog = () => {
    trackEvent("Catalog", "Create Project", "Close")
		projects.dialogBox.type === 'edit' ? dispatch(closeEditDialog()) : dispatch(closeNewDialog());
    setTimeout(() => {
      //dispatch(removeRequestDone());
      resetForm();
    }, 150);
	}

  const saveData = (data: any) => {
    const _data = JSON.parse(JSON.stringify({
      ...formData,
      ...data
    }))
    setFormData(_data)

  }

const createProject = () => {
  var projection = formData.projection;

  const data: any = {
      name: formData.name,
      file_date: Date.now(),
      description: formData.description,
      timezone: formData.timezone,
      coord_hdatum: formData.hdatum,
      coord_vdatum: formData.vdatum,
      coord_projection: formData.projection,
      voxel_size: formData.voxelSize,
      coord_origin_x: formData.originX,
      coord_origin_y: formData.originY,
      coord_origin_z: formData.originZ,
      coord_unit: formData.units,
      type: "PROJECT",
  };

  if (projection === "utm") {
      data["coord_projection_utm_northing"] = formData.utmNorthing;
      data["coord_projection_utm_easting"] = formData.utmZone;
  } else if (projection === "tm") {
      data["coord_projection_tm_falseEasting"] = formData.falseEasting;
      data["coord_projection_tm_falseNorthing"] = formData.falseNorthing;
      data["coord_projection_tm_latOriginDeg"] = formData.latitudeOrigin;
      data["coord_projection_tm_longMeridianDeg"] = formData.longitudeMeridian;
      data["coord_projection_tm_scaleFactor"] = formData.scaleFactor;
  } else if (projection === "lcc") {
      data["coord_projection_lcc_falseEasting"] = formData.falseEasting;
      data["coord_projection_lcc_falseNorthing"] = formData.falseNorthing;
      data["coord_projection_lcc_latOfOriginDeg"] = formData.latitudeOrigin;
      data["coord_projection_lcc_longOfOriginDeg"] = formData.longitudeMeridian;
      data["coord_projection_lcc_firstStdParallelDeg"] = formData.firstStdParallel;
      data["coord_projection_lcc_secondStdParallelDeg"] = formData.secondStdParallel;
  } else if (projection === "aeac") {
      data["coord_projection_aeac_falseEasting"] = formData.falseEasting;
      data["coord_projection_aeac_falseNorthing"] = formData.falseNorthing;
      data["coord_projection_aeac_latOfOriginDeg"] = formData.latitudeOrigin;
      data["coord_projection_aeac_longOfOriginDeg"] = formData.longitudeMeridian;
      data["coord_projection_aeac_firstStdParallelDeg"] = formData.firstStdParallel;
      data["coord_projection_aeac_secondStdParallelDeg"] = formData.secondStdParallel;
  } else if (projection === "amg") {
      data["coord_projection_amg_zone"] = formData.amgZone;
  } else if (projection === "mga") {
      data["coord_projection_mga_zone"] = formData.mgaZone;
  } else if (projection === "lo") {
      data["coord_projection_lo_zone"] = formData.loZone;
  }
  data["access_level"] = "full";
  const dispachAction = projects.dialogBox.type === 'edit' ? remoteUpdateProject : remoteCreateProject;
  dispatch(resetLastRequest());
	dispatch(dispachAction(data));
  setFormPayloadData(data);
  setSubmitForm(false);
}

  useEffect(() => {
    if (!submitForm)  return ;
    createProject();
  }, [submitForm]);

  const onSubmit = () => {
    setSubmitForm(true);
    trackEvent("Catalog", "Create Project", "Submit")
  };

  const _handleSubmit = () => {
    if (isLastStep) {
      onSubmit();
    } else {
      console.log("Step : " + activeStep)
      trackEvent("Catalog", "Create Project", steps[activeStep])
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  }

  const _handleBack = () => {
    if (activeStep === 1) {
      return;
    }
    setActiveStep(activeStep - 1);
  }

	useDeepCompareEffect(() => {
		if (lastRequest !== 'create' && lastRequest !== 'update'/* && lastRequest !== 'delete'*/) {
			return;
		}
		if (requestDone && success) {
		 // dispatch(addNotification(NotificationModel({ message: 'The project is successfully updated.', options: { variant: 'success' } })));
			//dispatch(removeRequestDone());
      //closeComposeDialog();
      if (lastRequest === 'create') {
        dispatch(addNotification(NotificationModel({ message: 'The project is successfully created.', options: { variant: 'success' } })));
        updateData("create", "projects", lastCreated.id, formPayloadData, (id: string) => {});
        //setTimeout(() => {
          //window.location.reload();
          EventsEmitter.emit("reloadData", "");
        //}, 100);
      }
      if (lastRequest === 'update') {
        dispatch(removeRequestDone());
        closeComposeDialog();
        dispatch(addNotification(NotificationModel({ message: 'The project is successfully updated.', options: { variant: 'success' } })));
        updateData("update", "projects", projectId, {
          ...formPayloadData,
          ID: projectId
        }, (id: string) => {});

        //setTimeout(() => {
          //window.location.reload();
          EventsEmitter.emit("reloadData", "");
        //}, 100);
        
      }
		}
		if (requestDone && error) {
			dispatch(addNotification(NotificationModel({ message: 'An error occurred updating the system.', options: { variant: 'error' } })));
			dispatch(removeRequestDone());
		}
		
	}, [dispatch, lastRequest, addNotification, success, error, requestDone, NotificationModel]);

  const DialogAction = () => {
    return (
      <ButtonStepper
        steps={steps}
        activeStep={activeStep}
        onClickBack={_handleBack}
        loading={isSubmitting}
        formId="projectForm"
      />
    )
  }

  const AddObjectsDialogAction = () => {
    return (
        <>
            <Button
                type="button"
                color="primary"
                variant="outlined"
                fullWidth
                sx={{
                    width: 105
                }}
                onClick={(e) => {
                  closeComposeDialog();
                }}
                aria-label="Cancel"
                title="Cancel"
            >
                Cancel
            </Button>
        </>
    )
  }

  const showObjectPanel = (e:any, objName:string) => {
    e.preventDefault()
    dispatch(addAutomaticObject({ objName, project: projectId || lastCreated.id }));
    closeComposeDialog();
    setTimeout(() => {
        history.push(`/catalog/${projectId || lastCreated.id}`);
    }, 250);
  }
  
  const AddObjectsDialog = (props:any) => {
      const { classes, cx } = useStyles();
  
      return (
          <>
              <Scrollbars
                  autoHeight
                  autoHeightMax={600}
                  style={{ overflowX: "hidden", width: "100%" }}
              >
                  <div style={{paddingRight: '10px'}}>
                      <div style={{ display:'flex', flexDirection:'column', justifyContent: 'space-between', width: "100%", paddingBottom: '0px'}}>
                          <p className={classes.helpText }>VoxelSpace supports a wide range of input formats. 
                              Select the format of the object you want to add to your project in the list below.
                              <Stack 
                                  gap={5}
                                  direction="row"
                                  sx={{
                                      //justifyContent: "space-between",
                                      alignItems: "center",
                                      padding: '25px 0'
                                  }}
                              >
                                  <div className={cx(classes.objectItem)}>
                                      <a href="/#" 
                                          onClick={(e) => {
                                              e.preventDefault()
                                              trackEvent("Raw Data", "Add Object", "Point Cloud");
                                              showObjectPanel(e, "Point Cloud");
                                          }}
                                      >
                                          <div><PointCloud  style={{height:"30px", width:"30px"}}/></div>
                                          <div>Point Cloud</div>
                                      </a>
                                  </div>
                                  <div className={cx(classes.objectItem)}>
                                      <a href="/#" 
                                          onClick={(e) => {
                                              e.preventDefault()
                                              trackEvent("Raw Data", "Add Object", "Mesh");
                                              showObjectPanel(e, "Mesh");
                                          }}
                                      >
                                          <div><Mesh  style={{height:"30px", width:"30px"}} /></div>
                                          <div>Mesh</div>
                                      </a>
                                  </div>
                                  <div className={cx(classes.objectItem)}>
                                      <a href="/#" 
                                          onClick={(e) => {
                                              e.preventDefault()
                                              trackEvent("Raw Data", "Add Object", "Block Model");
                                              showObjectPanel(e, "Block Model");
                                          }}
                                      >
                                          <div><BlockModel  style={{height:"30px", width:"30px"}} /></div>
                                          <div>Block Model</div>
                                      </a>
                                  </div>
                                  <div className={cx(classes.objectItem)}>
                                      <a href="/#" 
                                          onClick={(e) => {
                                              e.preventDefault()
                                              trackEvent("Raw Data", "Add Object", "Ortho-Imagery");
                                              showObjectPanel(e, "Ortho-Imagery");
                                          }}
                                      >
                                          <div><Ortho style={{height:"30px", width:"30px"}} /></div>
                                          <div>Ortho-Imagery</div>
                                      </a>
                                  </div>
                                  <div className={cx(classes.objectItem)}>
                                      <a href="/#" 
                                          onClick={(e) => {
                                              e.preventDefault()
                                              trackEvent("Raw Data", "Add Object", "Heightmap");
                                              showObjectPanel(e, "Heightmap");
                                          }}
                                      >
                                          <div><Heightmap style={{height:"30px", width:"30px"}} /></div>
                                          <div>Heightmap</div>
                                      </a>
                                  </div>
                              </Stack>
                          </p>
                      </div> 
                  </div>
              </Scrollbars>
          </>
      )
  
  }

  return (
    <FormContext.Provider value = {{ defaultValues, isLastStep, setIsLastStep, saveData, setIsSubmitting, _handleSubmit, currentFields }}>
      <CommonDialogBox
        classes={{
          paper: 'm-24 rounded-8'
        }}
        {...projects.dialogBox.props}
        handleClose={closeComposeDialog}
        helpLinkCode={createStatus ? "adding-spatial-entities-to-a-project" : projectHelpLinks[activeStep - 1]}
        fullWidth
        maxWidth={ createStatus ?  "md" : "sm"}
        subtitle1={ createStatus ? "Step 1: Add Spatial Entities" : (projects.dialogBox.type === 'new' ? 'New Project' : 'Project Edit')}
        titleh5={ createStatus ? "" : (projects.dialogBox.type === 'edit' ? formData.name : '') }
        dialogAction={ createStatus ? <AddObjectsDialogAction /> : <DialogAction />}
        displayScrollBar={false}
      >
        { createStatus ? (
            <AddObjectsDialog />
          ) : (
            <div>
              <Box pt={2}>
                <CustomStepper activeStep={activeStep} steps={steps} />
              </Box>
              <Divider sx={{ mt: 3,mb: 3,mr:2,ml:2 }} />
              <Scrollbars
                    autoHeight
                    autoHeightMax={300}
                    style={{ overflowX: "hidden", width: "100%," }}
              >
                <Box p={2}>
                  {_renderStepContent(activeStep)}
                </Box>
              </Scrollbars>
            </div>
          )}
      </CommonDialogBox>
    </FormContext.Provider>
  );
}

export default CreateProjectPanel;
